import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Priority, Status } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { generateAnalyticsSummary } from './aiService';
import AiSummary from '../maintenance/AiSummary';
import { fetchAnalytics } from './analyticsService';

import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

interface AnalyticsModalProps {
  selectedFacilityId: number;
  onClose: () => void;
}

type Tab = 'monthly' | 'daily' | 'yearly';

// Color mappings for chart segments.
const PRIORITY_COLORS: { [key in Priority]: string } = {
    [Priority.Critical]: 'hsla(355, 78%, 55%, 1)',
    [Priority.High]: 'hsla(28, 96%, 53%, 1)',
    [Priority.Medium]: 'hsla(48, 94%, 50%, 1)',
    [Priority.Low]: 'hsla(145, 63%, 49%, 1)',
};

const STATUS_COLORS: { [key in Status]: string } = {
    [Status.Open]: 'hsla(355, 78%, 55%, 1)',
    [Status.InProgress]: 'hsla(48, 94%, 50%, 1)',
    [Status.Completed]: 'hsla(145, 63%, 49%, 1)',
    [Status.Pending]: 'hsla(217, 91%, 60%, 1)',
};
const CREATED_COLOR = 'hsla(217, 91%, 60%, 0.7)';
const COMPLETED_COLOR = 'hsla(145, 63%, 49%, 0.7)';
const REPORTER_COLOR = 'hsla(265, 80%, 60%, 0.7)';
const ASSIGNEE_COLOR = 'hsla(190, 80%, 55%, 0.7)';


const AnalyticsModal: React.FC<AnalyticsModalProps> = ({ selectedFacilityId, onClose }) => {
  const [isShowing, setIsShowing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('monthly');
  const modalRef = useRef<HTMLDivElement>(null);

  // State for fetched data, loading, and errors
  const [analyticsData, setAnalyticsData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // State for AI Summaries
  const [summary, setSummary] = useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  
  // Refs for chart canvases and instances
  const chartRefs = {
      dailyPriority: useRef<HTMLCanvasElement>(null),
      monthlyCreatedVsCompleted: useRef<HTMLCanvasElement>(null),
      monthlyCategories: useRef<HTMLCanvasElement>(null),
      monthlyReporters: useRef<HTMLCanvasElement>(null),
      monthlyAssignees: useRef<HTMLCanvasElement>(null),
      yearlyTrends: useRef<HTMLCanvasElement>(null),
      yearlyCategories: useRef<HTMLCanvasElement>(null),
      yearlyReporters: useRef<HTMLCanvasElement>(null),
      yearlyAssignees: useRef<HTMLCanvasElement>(null),
  };
  const chartInstances = useRef<{ [key: string]: any }>({});

  useFocusTrap(modalRef, isShowing);

  useEffect(() => {
    setIsShowing(true);

    const loadAnalytics = async () => {
        setIsLoading(true);
        setFetchError(null);
        try {
            // Fetch data for all periods concurrently
            const [dailyResponse, monthlyResponse, yearlyResponse] = await Promise.all([
                fetchAnalytics('daily', selectedFacilityId),
                fetchAnalytics('monthly', selectedFacilityId),
                fetchAnalytics('yearly', selectedFacilityId),
            ]);
            // The API returns data nested under a key corresponding to the period.
            // We need to un-nest it to match the component's expected data structure.
            setAnalyticsData({
              daily: dailyResponse?.daily,
              monthly: monthlyResponse?.monthly,
              yearly: yearlyResponse?.yearly,
            });
        } catch (err) {
            setFetchError(err instanceof Error ? err.message : 'Failed to load analytics data.');
        } finally {
            setIsLoading(false);
        }
    };
    
    loadAnalytics();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFacilityId]); // Refetch if the facility changes

  const handleClose = () => {
    setIsShowing(false);
    setTimeout(onClose, 300);
  };

  useEffect(() => {
    // Guard against running chart logic before data is available
    if (!analyticsData) return;

    // Destroy all charts when tab changes
    Object.values(chartInstances.current).forEach((chart: any) => chart.destroy());
    chartInstances.current = {};
    
    // Helper to create charts
    const createChart = (ref: React.RefObject<HTMLCanvasElement>, config: any, name: string) => {
        if (ref.current) {
            const ctx = ref.current.getContext('2d');
            chartInstances.current[name] = new Chart(ctx, config);
        }
    };
    
    // Create charts for the active tab
    if (activeTab === 'daily') {
        const priorityData = analyticsData.daily?.activePriorityCounts ?? {};
        const priorityLabels = Object.keys(priorityData) as Priority[];
        createChart(chartRefs.dailyPriority, {
            type: 'doughnut',
            data: {
                labels: priorityLabels,
                datasets: [{
                    data: priorityLabels.map(p => priorityData[p] || 0),
                    backgroundColor: priorityLabels.map(p => PRIORITY_COLORS[p]),
                    borderColor: '#fff',
                    borderWidth: 2,
                }]
            },
            options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'right' }, title: { display: true, text: 'Active Workload by Priority', font: { size: 16 } } } }
        }, 'dailyPriority');
    } else if (activeTab === 'monthly') {
        const createdVsCompletedData = analyticsData.monthly?.createdVsCompleted ?? [];
        createChart(chartRefs.monthlyCreatedVsCompleted, {
            type: 'bar',
            data: {
                labels: createdVsCompletedData.map((d: any) => d.day),
                datasets: [
                    { label: 'Created', data: createdVsCompletedData.map((d: any) => d.created), backgroundColor: CREATED_COLOR },
                    { label: 'Completed', data: createdVsCompletedData.map((d: any) => d.completed), backgroundColor: COMPLETED_COLOR }
                ]
            },
            options: { responsive: true, maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } }, plugins: { title: { display: true, text: 'Created vs. Completed (Last 30 Days)', font: { size: 16 } } } }
        }, 'monthlyCreatedVsCompleted');
        
        const top5MonthlyCategories = Object.entries(analyticsData.monthly?.categoryCounts || {}).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
        createChart(chartRefs.monthlyCategories, {
            type: 'bar',
            data: {
                labels: top5MonthlyCategories.map(c => c[0]),
                datasets: [{ label: 'Task Count', data: top5MonthlyCategories.map(c => c[1]), backgroundColor: CREATED_COLOR }]
            },
            options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Top 5 Issue Categories (Last 30 Days)', font: { size: 16 } } } }
        }, 'monthlyCategories');
        
        const top5MonthlyReporters = Object.entries(analyticsData.monthly?.reporterCounts || {}).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
        createChart(chartRefs.monthlyReporters, {
            type: 'bar', data: { labels: top5MonthlyReporters.map(c => c[0]), datasets: [{ label: 'Tasks Reported', data: top5MonthlyReporters.map(c => c[1]), backgroundColor: REPORTER_COLOR }] },
            options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Top 5 Reporters (Last 30 Days)', font: { size: 16 } } } }
        }, 'monthlyReporters');

        const top5MonthlyAssignees = Object.entries(analyticsData.monthly?.assigneeCounts || {}).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
        createChart(chartRefs.monthlyAssignees, {
            type: 'bar', data: { labels: top5MonthlyAssignees.map(c => c[0]), datasets: [{ label: 'Tasks Assigned', data: top5MonthlyAssignees.map(c => c[1]), backgroundColor: ASSIGNEE_COLOR }] },
            options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Top 5 Assignees (Last 30 Days)', font: { size: 16 } } } }
        }, 'monthlyAssignees');

    } else if (activeTab === 'yearly') {
        const trendsData = analyticsData.yearly?.trends ?? [];
        createChart(chartRefs.yearlyTrends, {
            type: 'line',
            data: {
                labels: trendsData.map((t: any) => t.month),
                datasets: [{ label: 'Tasks Created', data: trendsData.map((t: any) => t.created), borderColor: CREATED_COLOR, tension: 0.1, fill: false }]
            },
            options: { responsive: true, maintainAspectRatio: false, plugins: { title: { display: true, text: 'Month-Over-Month Task Volume', font: { size: 16 } } } }
        }, 'yearlyTrends');
        
        const top5YearlyCategories = Object.entries(analyticsData.yearly?.categoryCounts || {}).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
        createChart(chartRefs.yearlyCategories, {
            type: 'bar',
            data: {
                labels: top5YearlyCategories.map(c => c[0]),
                datasets: [{ label: 'Task Count', data: top5YearlyCategories.map(c => c[1]), backgroundColor: CREATED_COLOR }]
            },
            options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Top 5 Recurring Issues (Last Year)', font: { size: 16 } } } }
        }, 'yearlyCategories');

        const top5YearlyReporters = Object.entries(analyticsData.yearly?.reporterCounts || {}).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
        createChart(chartRefs.yearlyReporters, {
            type: 'bar', data: { labels: top5YearlyReporters.map(c => c[0]), datasets: [{ label: 'Tasks Reported', data: top5YearlyReporters.map(c => c[1]), backgroundColor: REPORTER_COLOR }] },
            options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Top 5 Reporters (Last Year)', font: { size: 16 } } } }
        }, 'yearlyReporters');
        
        const top5YearlyAssignees = Object.entries(analyticsData.yearly?.assigneeCounts || {}).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
        createChart(chartRefs.yearlyAssignees, {
            type: 'bar', data: { labels: top5YearlyAssignees.map(c => c[0]), datasets: [{ label: 'Tasks Assigned', data: top5YearlyAssignees.map(c => c[1]), backgroundColor: ASSIGNEE_COLOR }] },
            options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: true, text: 'Top 5 Assignees (Last Year)', font: { size: 16 } } } }
        }, 'yearlyAssignees');
    }

    return () => {
        Object.values(chartInstances.current).forEach((chart: any) => chart?.destroy());
    };
  }, [activeTab, analyticsData]);
  
  const handleSummarize = async () => {
    if (!analyticsData) return;
    setIsSummarizing(true);
    setSummary(null);
    setSummaryError(null);
    try {
      let dataToSummarize = {};
      let timeFrame = '';
      if (activeTab === 'monthly') {
          dataToSummarize = analyticsData.monthly;
          timeFrame = 'Monthly';
      } else if (activeTab === 'yearly') {
          dataToSummarize = analyticsData.yearly;
          timeFrame = 'Yearly';
      }
      const result = await generateAnalyticsSummary(dataToSummarize, timeFrame);
      setSummary(result);
    } catch (err) {
      setSummaryError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSummarizing(false);
    }
  };


  const renderContent = () => {
    switch (activeTab) {
      case 'daily':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <KPICard title="New Tasks Today" value={analyticsData.daily?.newToday ?? 0} icon="new" />
                <KPICard title="Completed Today" value={analyticsData.daily?.completedToday ?? 0} icon="completed" />
                <KPICard title="Currently Overdue" value={analyticsData.daily?.overdue ?? 0} icon="overdue" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="h-80 relative"><canvas ref={chartRefs.dailyPriority}></canvas></div>
              </div>
              <div className="space-y-6">
                <LeaderboardCard title="Top Reporters (Today)" data={analyticsData.daily?.topReportersToday ?? {}} />
                <LeaderboardCard title="Top Assignees (Active)" data={analyticsData.daily?.topAssigneesActive ?? {}} />
              </div>
            </div>
          </div>
        );
      case 'monthly':
        return (
          <div className="space-y-6">
             <AiSummaryWithButton 
                summary={summary} error={summaryError} isSummarizing={isSummarizing}
                onSummarize={handleSummarize} onDismiss={() => { setSummary(null); setSummaryError(null); }}
             />
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.monthlyCreatedVsCompleted}></canvas></div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.monthlyCategories}></canvas></div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.monthlyAssignees}></canvas></div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.monthlyReporters}></canvas></div>
                </div>
            </div>
          </div>
        );
      case 'yearly':
        return (
           <div className="space-y-6">
             <AiSummaryWithButton 
                summary={summary} error={summaryError} isSummarizing={isSummarizing}
                onSummarize={handleSummarize} onDismiss={() => { setSummary(null); setSummaryError(null); }}
             />
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.yearlyTrends}></canvas></div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.yearlyCategories}></canvas></div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.yearlyAssignees}></canvas></div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="h-80 relative"><canvas ref={chartRefs.yearlyReporters}></canvas></div>
                </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black z-50 flex justify-center items-center transition-opacity duration-300 ${isShowing ? 'bg-opacity-60' : 'bg-opacity-0'}`}
      aria-labelledby="analytics-modal-title" role="dialog" aria-modal="true" onClick={handleClose}
    >
      <div 
        ref={modalRef}
        className={`bg-gray-100 rounded-xl shadow-2xl w-full max-w-5xl m-4 transform transition-all duration-300 ease-out flex flex-col max-h-[calc(100vh-80px)] ${isShowing ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        role="document" onClick={(e) => e.stopPropagation()}
      >
        <header className="p-5 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 id="analytics-modal-title" className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-teal-600" viewBox="0 0 20 20" fill="currentColor"><path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" /><path d="M12 2.252A8.014 8.014 0 0117.748 12H12V2.252z" /></svg>
              Maintenance Analytics
            </h2>
            <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600 rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <nav className="mt-4 border-b border-gray-200">
            <div className="flex space-x-4">
                <TabButton label="Monthly" isActive={activeTab === 'monthly'} onClick={() => { setActiveTab('monthly'); setSummary(null); setSummaryError(null); }} />
                <TabButton label="Daily" isActive={activeTab === 'daily'} onClick={() => { setActiveTab('daily'); setSummary(null); setSummaryError(null); }} />
                <TabButton label="Yearly" isActive={activeTab === 'yearly'} onClick={() => { setActiveTab('yearly'); setSummary(null); setSummaryError(null); }} />
            </div>
          </nav>
        </header>

        <main className="p-6 overflow-y-auto">
             {isLoading ? (
                <div className="flex justify-center items-center h-96">
                    <svg className="animate-spin h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="ml-4 text-lg text-gray-600">Loading Analytics...</span>
                </div>
            ) : fetchError ? (
                <div className="text-center p-8 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    <h3 className="text-xl font-bold mb-2">Failed to load analytics</h3>
                    <p>{fetchError}</p>
                </div>
            ) : analyticsData ? (
                renderContent()
            ) : null}
        </main>
      </div>
    </div>
  );
};

const LeaderboardCard: React.FC<{ title: string; data: Record<string, number> }> = ({ title, data }) => {
    const sortedData = useMemo(() => {
        return Object.entries(data).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 5);
    }, [data]);

    return (
        <div className="bg-white p-6 rounded-lg shadow-md h-full">
            <h4 className="text-md font-semibold text-gray-700 mb-4">{title}</h4>
            {sortedData.length > 0 ? (
                <ul className="space-y-3">
                    {sortedData.map(([name, count], index) => (
                        <li key={name} className="flex justify-between items-center text-sm">
                            <div className="flex items-center">
                                <span className="font-bold text-gray-500 w-6 text-center mr-2">{index + 1}</span>
                                <span className="text-gray-800 font-medium">{name}</span>
                            </div>
                            <span className="font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">{count}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No data available.</p>
                </div>
            )}
        </div>
    );
};


const TabButton: React.FC<{ label: string; isActive: boolean; onClick: () => void }> = ({ label, isActive, onClick }) => (
    <button onClick={onClick} className={`px-3 py-2 font-medium text-sm rounded-t-md focus:outline-none transition-colors ${isActive ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'}`}>
        {label}
    </button>
);

const KPICard: React.FC<{ title: string; value: number | string; icon: 'new' | 'completed' | 'overdue' }> = ({ title, value, icon }) => {
    const icons = {
        new: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
        completed: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
        overdue: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    };
    const colors = {
        new: 'bg-blue-100',
        completed: 'bg-green-100',
        overdue: 'bg-red-100',
    };
    return (
        <div className="bg-white p-6 rounded-lg shadow-md flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-bold text-gray-800">{value}</p>
            </div>
            <div className={`${colors[icon]} p-3 rounded-full`}>
                {icons[icon]}
            </div>
        </div>
    );
};

const AiSummaryWithButton: React.FC<{
    summary: string | null; error: string | null; isSummarizing: boolean;
    onSummarize: () => void; onDismiss: () => void;
}> = ({ summary, error, isSummarizing, onSummarize, onDismiss }) => {
    return (
        <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">AI-Powered Summary</h3>
                <button onClick={onSummarize} disabled={isSummarizing} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                    {isSummarizing ? (
                        <>
                            <svg className="animate-spin -ml-1 h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            Generating...
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                            Generate Summary
                        </>
                    )}
                </button>
            </div>
            <AiSummary summary={summary} error={error} onDismiss={onDismiss} />
        </div>
    )
}


export default AnalyticsModal;